'use client';

import { useCart, CartItem } from '@/lib/hooks/useCart';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Package, ShoppingCart } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { CartButton } from '@/components/CartButton';

export function CartHoverPreview() {
  const { cart } = useCart();
  const router = useRouter();

  const handleGoToCart = () => {
    router.push('/cart');
  };

  const renderDeliveryInfo = (item: CartItem) => {
    if (item.fromDelivery > 0) {
      return (
        <div className="text-xs text-muted-foreground">
          {item.fromStock} szt. ze stanu,{' '}
          {item.fromDelivery} szt. z dostawy
        </div>
      );
    }
    return null;
  };

  const itemCount = cart?.items?.length ?? 0;
  const cartTotal = cart?.items?.reduce((sum, item) => sum + (item.product.price * item.quantity), 0) ?? 0;

  return (
    <HoverCard openDelay={100} closeDelay={300}>
      <HoverCardTrigger asChild>
        <div>
          <CartButton />
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 p-4" align="end" side="bottom" sideOffset={8}>
        <div className="space-y-2">
          <h4 className="font-medium">Twój koszyk</h4>
          <p className="text-sm text-muted-foreground">
            {itemCount > 0
              ? `${itemCount} ${itemCount === 1 ? 'produkt' : 'produkty'} w koszyku`
              : 'Koszyk jest pusty'}
          </p>
        </div>

        {cart?.items && cart.items.length > 0 ? (
          <>
            <div className="mt-4 pb-3 border-b space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Suma:</span>
                <span className="font-bold">
                  {cartTotal.toFixed(2)} zł
                </span>
              </div>
              <Button className="w-full" onClick={handleGoToCart}>
                Przejdź do koszyka
              </Button>
            </div>

            <div className="mt-3 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
              <div className="space-y-3">
                {cart.items.slice(0, 4).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 text-sm border-b pb-2">
                    <div className="flex items-center gap-2 w-full">
                      <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                        {item.product.image ? (
                          <div className="relative w-full h-full overflow-hidden rounded-md">
                            <Image 
                              src={item.product.image} 
                              alt={item.product.name} 
                              fill
                              sizes="32px"
                              className="object-cover"
                            />
                          </div>
                        ) : (
                          <Package className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{item.product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.quantity} x {item.product.price.toFixed(2)} zł
                        </p>
                        {renderDeliveryInfo(item)}
                      </div>
                    </div>
                  </div>
                ))}

                {cart.items.length > 4 && (
                  <div className="text-center mt-2">
                    <p className="text-xs text-muted-foreground mb-1">
                      + {cart.items.length - 4} więcej produktów
                    </p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs w-full py-1 h-auto"
                      onClick={handleGoToCart}
                    >
                      Zobacz wszystkie produkty
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="mt-4 text-center py-4">
            <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">Twój koszyk jest pusty</p>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
} 